import React, { useMemo } from 'react'
import PropTypes from 'prop-types'

function getTankProfileData(data, steps = 120) {
    if (!Array.isArray(data) || data.length < 2) return null
    // Sort by voltage (sensor level)
    const sorted = [...data].sort((a, b) => a.voltage - b.voltage)
    const V = sorted.map(d => Number(d.quantity))
    const H = sorted.map(d => Number(d.voltage))
    // Normalize height
    const minH = Math.min(...H)
    const maxH = Math.max(...H)
    const hNorm = H.map(h => (h - minH) / (maxH - minH))
    // dV/dH (guard against division by zero)
    const dVdH = V.map((v, i) => {
        if (i === 0) return (V[1] - V[0]) / (H[1] - H[0] || 1)
        if (i === V.length - 1) return (V[i] - V[i - 1]) / (H[i] - H[i - 1] || 1)
        return (V[i + 1] - V[i - 1]) / (H[i + 1] - H[i - 1] || 1)
    })
    // Guard negatives
    const dVdHClipped = dVdH.map(x => Math.max(0, x))
    // width ∝ sqrt(dV/dH)
    let halfW = dVdHClipped.map(Math.sqrt)
    const maxW = Math.max(...halfW)
    halfW = halfW.map(w => w / (maxW || 1))
    // Interpolate outline (linear for simplicity)
    const yArr = Array.from({ length: steps }, (_, i) => i / (steps - 1))
    function interp(xArr, yArr, x) {
        if (x <= xArr[0]) return yArr[0]
        if (x >= xArr[xArr.length - 1]) return yArr[yArr.length - 1]
        for (let i = 0; i < xArr.length - 1; i++) {
            if (xArr[i] <= x && x < xArr[i + 1]) {
                const t = (x - xArr[i]) / (xArr[i + 1] - xArr[i])
                return yArr[i] * (1 - t) + yArr[i + 1] * t
            }
        }
        return yArr[yArr.length - 1]
    }
    const wArr = yArr.map(y => interp(hNorm, halfW, y))
    const qtyLabels = yArr.map(y => interp(hNorm, V, y))
    const voltageLabels = yArr.map(y => interp(hNorm, H, y))
    return { yArr, wArr, qtyLabels, voltageLabels, minH, maxH, minV: Math.min(...V), maxV: Math.max(...V) }
}

function lerpColor(a, b, t) {
    // a, b: [r,g,b], t: 0-1
    return `rgb(${Math.round(a[0] + (b[0] - a[0]) * t)},${Math.round(a[1] + (b[1] - a[1]) * t)},${Math.round(a[2] + (b[2] - a[2]) * t)})`
}

function FuelTankProfileChart({ data, width = 1000, height = 600 }) {
    const profile = useMemo(() => getTankProfileData(data, 120), [data])
    if (!profile) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Not enough data</div>
    const { yArr, wArr, qtyLabels, voltageLabels, minH, maxH, minV, maxV } = profile
    // SVG coordinates: y=0 at top, y=height at bottom
    // Map yArr (0-1) to SVG y (height to 0)
    const ySvg = yArr.map(y => height - y * (height - 80) - 40)
    // Map wArr (0-1) to SVG x (-1 to 1 normalized)
    const xNorm = wArr.map(w => w)
    const tankLeft = xNorm.map(x => width / 2 - x * (width * 0.45))
    const tankRight = xNorm.map(x => width / 2 + x * (width * 0.45))
    // For horizontal bands
    const bandCount = yArr.length - 1
    // Blue gradient: bottom = [21,101,192], top = [227,242,253]
    const colorA = [21, 101, 192]
    const colorB = [227, 242, 253]
    // Axis ticks (6 for each axis)
    const tickCount = 6
    const tickIdx = Array.from({ length: tickCount }, (_, i) => Math.round(i * (yArr.length - 1) / (tickCount - 1)))
    // Axis positions
    const axisPad = 60
    return (
        <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ width: '100%', maxWidth: width, height: 'auto', display: 'block' }}>
            {/* Tank bands */}
            {Array.from({ length: bandCount }).map((_, i) => {
                const points = [
                    [tankLeft[i], ySvg[i]],
                    [tankLeft[i + 1], ySvg[i + 1]],
                    [tankRight[i + 1], ySvg[i + 1]],
                    [tankRight[i], ySvg[i]]
                ]
                const t = i / (bandCount - 1)
                return (
                    <polygon
                        key={i}
                        points={points.map(p => p.join(',')).join(' ')}
                        fill={lerpColor(colorA, colorB, 1 - t)}
                        opacity="0.95"
                    />
                )
            })}
            {/* Tank outline */}
            <polyline
                points={tankLeft.map((x, i) => `${x},${ySvg[i]}`).join(' ')}
                fill="none"
                stroke="#222"
                strokeWidth="2"
            />
            <polyline
                points={tankRight.map((x, i) => `${x},${ySvg[i]}`).join(' ')}
                fill="none"
                stroke="#222"
                strokeWidth="2"
            />
            {/* Centerline */}
            <line x1={width / 2} y1={ySvg[0]} x2={width / 2} y2={ySvg[ySvg.length - 1]} stroke="#222" strokeDasharray="10,7" strokeWidth="3" />
            {/* Y-axis: Quantity (left) */}
            {tickIdx.map(idx => (
                <g key={idx}>
                    <line x1={axisPad - 8} y1={ySvg[idx]} x2={axisPad} y2={ySvg[idx]} stroke="#888" strokeWidth="1.5" />
                    <text x={axisPad - 12} y={ySvg[idx] + 6} fontSize="18" textAnchor="end" fill="#333">{Math.round(qtyLabels[idx])}</text>
                </g>
            ))}
            <text x={axisPad - 18} y={ySvg[0] - 16} fontSize="22" textAnchor="end" fill="#333" fontWeight="bold">L</text>
            <text x={axisPad + 10} y={height / 2} fontSize="20" textAnchor="middle" fill="#333" transform={`rotate(-90,${axisPad + 10},${height / 2})`}>Fuel Quantity (L)</text>
            {/* Y-axis: Voltage (right) */}
            {tickIdx.map(idx => (
                <g key={idx}>
                    <line x1={width - axisPad} y1={ySvg[idx]} x2={width - axisPad + 8} y2={ySvg[idx]} stroke="#888" strokeWidth="1.5" />
                    <text x={width - axisPad + 12} y={ySvg[idx] + 6} fontSize="18" textAnchor="start" fill="#333">{Math.round(voltageLabels[idx])}</text>
                </g>
            ))}
            <text x={width - axisPad + 18} y={ySvg[0] - 16} fontSize="22" textAnchor="start" fill="#333" fontWeight="bold">V</text>
            <text x={width - axisPad - 10} y={height / 2} fontSize="20" textAnchor="middle" fill="#333" transform={`rotate(-90,${width - axisPad - 10},${height / 2})`}>Sensor Level (units)</text>
            {/* X-axis label */}
            <text x={width / 2} y={height - 18} fontSize="22" textAnchor="middle" fill="#333">Tank width (normalised)</text>
            {/* Title */}
            {/* <text x={width / 2} y={38} fontSize="28" textAnchor="middle" fill="#222" fontWeight="bold">Fuel-tank cross-section with gradient fill</text> */}
        </svg>
    )
}

FuelTankProfileChart.propTypes = {
    data: PropTypes.arrayOf(
        PropTypes.shape({
            quantity: PropTypes.number.isRequired,
            voltage: PropTypes.number.isRequired
        })
    ).isRequired,
    width: PropTypes.number,
    height: PropTypes.number
}

export default FuelTankProfileChart 